/**
 * File GC Background Task — periodically removes orphaned files from db.files.
 *
 * Runs as a background task on app-start with a 30s delay — the last thing
 * to run after sync, doc processing, migrations, and index cleanup are done.
 *
 * Internally uses cleanOrphanedFiles() which has its own weekly throttle
 * via db.setting (synced), so multiple app-starts are cheap (just a setting read).
 *
 * @see fileGC.ts — core GC logic with 5 safety gates
 * @see docs/plans/FILE_GC_PLAN.md — full design and rationale
 */

import type { BackgroundTaskDef } from "@/background/types"
import { waitForInitialSync } from "@/shared/utils/sync/waitForInitialSync"
import { db } from "@/db/schema"
import { cleanOrphanedFiles } from "@/services/doc-processing/fileGC"

export const fileGcTask: BackgroundTaskDef = {
  id: "file-gc",
  description: "Garbage-collect orphaned files not referenced by any Y.Doc",
  priority: 15, // Lowest — run after migrations (3), day-change (5), index cleanup (10)
  triggers: ["app-start"],
  initialDelayMs: 30_000, // 30s — let everything else settle first

  async run(_signal) {
    // Wait for sync to complete before scanning
    const synced = await waitForInitialSync(db)
    if (!synced) return

    const result = await cleanOrphanedFiles()
    if (result.skipped) return
    if (result.deleted > 0) {
      console.info(
        `[FileGC] Cleaned ${result.deleted} orphaned files ` +
          `(${result.scanned} total, ${result.scanned - result.orphaned} referenced)`,
      )
    }
  },
}
